"use client";

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSession } from '@/context/SessionContext';
import { useAddressManager } from '@/context/AddressManagerContext';
import { supabase } from '@/lib/supabase/client';
import { getUserAddresses, type UserAddress } from '@/lib/supabase/userAddresses';
import { MapPin, LocateFixed, Home, ChevronLeft, ChevronRight, Package, CreditCard, Users, Truck, Plus, Store, RotateCw, Link, QrCode } from '@/components/icons';
import { motion } from 'framer-motion';
import ThreeDEffect from '@/components/ThreeDEffect';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { DEFAULT_LOCATIONS, findCityLandmarkByDisplayName, findNearestCityLandmarkByCoords } from '@/lib/defaultLocations';

export interface NearbyStore {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  avatar_url: string | null;
  latitude: number;
  longitude: number;
  distance?: number; // km
  schedule?: any;
  timezone?: string | null;
  schedule_timezone?: string | null;
}

interface StoreScheduleSnapshot {
  schedule: any;
  timezone: string | null;
  schedule_timezone: string | null;
}

type Mode = 'landmark' | 'home' | 'gps';
type SelectorViewMode = 'carousel' | 'directory';
interface LandmarkSnapshot {
  label: string;              // Nombre de la ciudad
  landmarkName: string;       // Nombre del landmark (ej: "Plaza 9 de Julio")
  latitude: number;
  longitude: number;
}

interface NearbyStoresSelectorProps {
  selectedStoreId?: string;
  pinnedStore?: Partial<NearbyStore> | null;
  onStoreSelect?: (store: NearbyStore, options?: { triggerAutoScroll?: boolean }) => void;
  onDistanceChipClick?: (store: NearbyStore) => void;
  onPromoCardSelectionChange?: (selected: boolean) => void;
  onViewModeChange?: (mode: SelectorViewMode) => void;
  onLoadingChange?: (loading: boolean) => void;
  renderStoreExpansion?: (store: NearbyStore) => React.ReactNode;
  renderStoreCatalog?: (store: NearbyStore) => React.ReactNode;
  onRentStoreClick?: () => void | Promise<void>;
  rentStoreLoading?: boolean;
}

interface KeywordChipsMarqueeProps {
  keywords: string[];
  storeId: string;
  animate: boolean;
}

function KeywordChipsMarquee({ keywords, storeId, animate }: KeywordChipsMarqueeProps) {
  const viewportRef = React.useRef<HTMLDivElement | null>(null);
  const trackRef = React.useRef<HTMLDivElement | null>(null);
  const chipRefs = React.useRef<Array<HTMLSpanElement | null>>([]);
  const [hasOverflow, setHasOverflow] = React.useState(false);
  const [stepOffsets, setStepOffsets] = React.useState<number[]>([0]);
  const [stepIndex, setStepIndex] = React.useState(0);

  const visibleKeywords = React.useMemo(() => keywords.filter(Boolean), [keywords]);

  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;

    const measure = () => {
      const maxScrollLeft = Math.max(0, track.scrollWidth - viewport.clientWidth);
      const overflow = maxScrollLeft > 6;
      setHasOverflow(overflow);

      if (!overflow) {
        setStepOffsets([0]);
        setStepIndex(0);
        return;
      }

      const rawOffsets = chipRefs.current
        .map((chip) => (chip ? chip.offsetLeft : 0))
        .filter((offset) => Number.isFinite(offset));

      const normalizedOffsets = Array.from(new Set([
        0,
        ...rawOffsets.map((offset) => Math.min(offset, maxScrollLeft)),
        maxScrollLeft,
      ].map((offset) => Math.round(offset))))
        .sort((a, b) => a - b);

      setStepOffsets(normalizedOffsets.length > 0 ? normalizedOffsets : [0]);
      setStepIndex((prev) => (prev < normalizedOffsets.length ? prev : 0));
    };

    measure();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        measure();
      });
      resizeObserver.observe(viewport);
      resizeObserver.observe(track);
    }

    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      resizeObserver?.disconnect();
    };
  }, [visibleKeywords]);

  const shouldStep = animate && hasOverflow && stepOffsets.length > 1;

  useEffect(() => {
    if (!shouldStep) {
      setStepIndex(0);
      return;
    }

    const interval = window.setInterval(() => {
      setStepIndex((prev) => (prev + 1) % stepOffsets.length);
    }, 1800);

    return () => window.clearInterval(interval);
  }, [shouldStep, stepOffsets]);

  if (visibleKeywords.length === 0) {
    return (
      <div className="relative z-10 mt-auto flex flex-wrap gap-1.5">
        <span className="inline-flex items-center rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-[11px] font-medium">
          Ver tienda
        </span>
      </div>
    );
  }

  const currentOffset = shouldStep ? stepOffsets[stepIndex] || 0 : 0;

  return (
    <div ref={viewportRef} className="relative z-10 mt-auto flex justify-start overflow-hidden text-left">
      <div
        ref={trackRef}
        className="inline-flex w-max items-center gap-1.5 whitespace-nowrap pr-1 will-change-transform"
        style={
          shouldStep
            ? { transform: `translateX(-${currentOffset}px)`, transition: 'transform 450ms ease' }
            : undefined
        }
      >
        {visibleKeywords.map((keyword, idx) => (
          <span
            ref={(node) => {
              chipRefs.current[idx] = node;
            }}
            key={`${storeId}-${keyword}-${idx}`}
            className="inline-flex items-center rounded-full bg-primary/10 text-primary border border-primary/20 px-2 py-0.5 text-[11px] font-medium"
          >
            {keyword}
          </span>
        ))}
      </div>
    </div>
  );
}

const KEYWORD_STOPWORDS = new Set([
  'de', 'del', 'la', 'las', 'el', 'los', 'con', 'sin', 'para', 'por', 'una', 'uno', 'unos', 'unas',
  'y', 'o', 'a', 'al', 'en', 'un', 'kg', 'gr', 'ml', 'lt', 'pack', 'x', 'the', 'and', 'off'
]);
const INITIAL_KEYWORD_BATCH_SIZE = 6;

const extractFallbackKeywords = (text: string): string[] => Array.from(new Set(
  (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split(/[^a-z0-9]+/g)
    .map((token) => token.trim())
    .filter((token) => token.length >= 3 && !KEYWORD_STOPWORDS.has(token))
)).slice(0, 3);

const extractKeywordsFromTitles = (titles: string[], max = 4): string[] => {
  const keywordCounts = new Map<string, number>();

  for (const title of titles || []) {
    const tokens = (title || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .split(/[^a-z0-9]+/g)
      .map((token) => token.trim())
      .filter((token) => token.length >= 3 && !KEYWORD_STOPWORDS.has(token));

    for (const token of tokens) {
      keywordCounts.set(token, (keywordCounts.get(token) || 0) + 1);
    }
  }

  return Array.from(keywordCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, max)
    .map(([keyword]) => keyword);
};

const mergeKeywordsWithDescription = (
  productKeywords: string[],
  store: Pick<NearbyStore, 'name' | 'slug' | 'description'> | undefined,
): string[] => {
  const descriptionKeywords = extractFallbackKeywords(store?.description || '');
  const fallbackKeywords = descriptionKeywords.length > 0
    ? descriptionKeywords
    : extractFallbackKeywords(`${store?.name || ''} ${store?.slug || ''}`);

  const merged: string[] = [];
  for (const keyword of [...productKeywords.slice(0, 2), ...fallbackKeywords, ...productKeywords.slice(2)]) {
    if (!merged.includes(keyword)) merged.push(keyword);
    if (merged.length >= 4) break;
  }
  return merged;
};

// Haversine formula (returns km)
function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
}

function isUndefinedCityLabel(value?: string | null): boolean {
  const normalized = (value || '').trim().toLowerCase();
  return !normalized || normalized === 'ciudad no definida' || normalized === 'ciudad desconocida';
}

function deriveLandmarkSnapshot(relevantLocation: any): LandmarkSnapshot {
  const nearestCity =
    relevantLocation && Number.isFinite(relevantLocation.latitude) && Number.isFinite(relevantLocation.longitude)
      ? findNearestCityLandmarkByCoords(relevantLocation.latitude, relevantLocation.longitude)
      : null;

  const matchedByName =
    relevantLocation?.displayName && !isUndefinedCityLabel(relevantLocation.displayName)
      ? findCityLandmarkByDisplayName(relevantLocation.displayName)
      : null;

  const fallback = DEFAULT_LOCATIONS.SALTA;
  const selectedLandmark = nearestCity || matchedByName || fallback;
  const label =
    nearestCity?.city ||
    matchedByName?.city ||
    (!isUndefinedCityLabel(relevantLocation?.displayName)
      ? String(relevantLocation.displayName).split(',')[0]?.trim()
      : null) ||
    'Salta';

  return {
    label,
    landmarkName: selectedLandmark.landmarkName || selectedLandmark.city || label,
    latitude: relevantLocation?.latitude ?? nearestCity?.latitude ?? matchedByName?.latitude ?? fallback.latitude,
    longitude: relevantLocation?.longitude ?? nearestCity?.longitude ?? matchedByName?.longitude ?? fallback.longitude,
  };
}

function normalizeSchedule(input: any): any {
  if (!input) return null;

  if (Array.isArray(input)) return input;

  if (typeof input === 'object') {
    if (Array.isArray((input as any).week)) return input;
    return null;
  }

  if (typeof input === 'string') {
    try {
      const parsed = JSON.parse(input);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object' && Array.isArray((parsed as any).week)) return parsed;
    } catch {
      return null;
    }
  }

  return null;
}

function getStoreWeekAndTimezone(storeObj: any): { week: any[]; tz: string } {
  const schedule = storeObj?.schedule;
  let week: any[] = [];
  let timezone: string | null = null;

  if (Array.isArray(schedule)) {
    week = schedule;
    timezone = storeObj?.timezone || storeObj?.schedule_timezone || null;
  } else if (schedule && typeof schedule === 'object' && Array.isArray((schedule as any).week)) {
    week = (schedule as any).week;
    timezone = (schedule as any).timezone || storeObj?.timezone || storeObj?.schedule_timezone || null;
  }

  return {
    week,
    tz: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  };
}

function getStoreNowParts(timezone: string) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date());

  const weekday = parts.find((part) => part.type === 'weekday')?.value || 'Mon';
  const hour = parseInt(parts.find((part) => part.type === 'hour')?.value || '0', 10);
  const minute = parseInt(parts.find((part) => part.type === 'minute')?.value || '0', 10);
  const weekdayMap: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

  return {
    dayIdx: weekdayMap[weekday] ?? 0,
    currentMin: (Number.isNaN(hour) ? 0 : hour) * 60 + (Number.isNaN(minute) ? 0 : minute),
  };
}

function isStoreOpenNow(storeObj: any): boolean {
  try {
    const { week, tz } = getStoreWeekAndTimezone(storeObj);
    if (!Array.isArray(week) || week.length === 0) return false;
    const { dayIdx, currentMin } = getStoreNowParts(tz);
    const periods = week[dayIdx] || [];

    for (const period of periods) {
      if (!period?.start || !period?.end) continue;
      const startMatch = period.start.match(/^(\d{2}):(\d{2})$/);
      const endMatch = period.end.match(/^(\d{2}):(\d{2})$/);
      if (!startMatch || !endMatch) continue;
      const startMin = parseInt(startMatch[1], 10) * 60 + parseInt(startMatch[2], 10);
      const endMin = parseInt(endMatch[1], 10) * 60 + parseInt(endMatch[2], 10);
      if (startMin <= currentMin && currentMin < endMin) return true;
    }

    return false;
  } catch {
    return false;
  }
}

function getTodayClosingTime(storeObj: any): string | null {
  try {
    const { week, tz } = getStoreWeekAndTimezone(storeObj);
    if (!Array.isArray(week) || week.length === 0) return null;
    const { dayIdx, currentMin } = getStoreNowParts(tz);
    const periods = week[dayIdx] || [];

    for (const period of periods) {
      if (!period?.start || !period?.end) continue;
      const startMatch = period.start.match(/^(\d{2}):(\d{2})$/);
      const endMatch = period.end.match(/^(\d{2}):(\d{2})$/);
      if (!startMatch || !endMatch) continue;
      const startMin = parseInt(startMatch[1], 10) * 60 + parseInt(startMatch[2], 10);
      const endMin = parseInt(endMatch[1], 10) * 60 + parseInt(endMatch[2], 10);
      if (startMin <= currentMin && currentMin < endMin) return period.end;
    }

    return null;
  } catch {
    return null;
  }
}

function getNextOpeningTime(storeObj: any): { time: string; dayIdx: number } | null {
  try {
    const { week, tz } = getStoreWeekAndTimezone(storeObj);
    if (!Array.isArray(week) || week.length === 0) return null;
    const { dayIdx: todayIdx, currentMin } = getStoreNowParts(tz);

    const todayPeriods = week[todayIdx] || [];
    for (const period of todayPeriods) {
      if (!period?.start) continue;
      const startMatch = period.start.match(/^(\d{2}):(\d{2})$/);
      if (!startMatch) continue;
      const startMin = parseInt(startMatch[1], 10) * 60 + parseInt(startMatch[2], 10);
      if (startMin > currentMin) return { time: period.start, dayIdx: todayIdx };
    }

    for (let offset = 1; offset <= 7; offset++) {
      const dayIdx = (todayIdx + offset) % 7;
      const periods = week[dayIdx] || [];
      for (const period of periods) {
        if (!period?.start) continue;
        const startMatch = period.start.match(/^(\d{2}):(\d{2})$/);
        if (!startMatch) continue;
        return { time: period.start, dayIdx };
      }
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Returns the milliseconds until the next schedule boundary (open or close)
 * across all provided stores. Caps at 1 hour; minimum 30 seconds.
 */
function msUntilNextBoundary(stores: Array<{ schedule?: any; timezone?: string | null; schedule_timezone?: string | null }>): number {
  const MAX_WAIT_MS = 60 * 60 * 1000;
  const MIN_WAIT_MS = 30 * 1000;
  let soonestDeltaMin = Infinity;

  for (const store of stores) {
    try {
      const { week, tz } = getStoreWeekAndTimezone(store);
      if (!Array.isArray(week) || week.length === 0) continue;
      const { dayIdx, currentMin } = getStoreNowParts(tz);

      for (let dayOffset = 0; dayOffset <= 1; dayOffset++) {
        const idx = (dayIdx + dayOffset) % 7;
        const periods = week[idx] || [];
        for (const p of periods) {
          for (const timeStr of [p?.start, p?.end]) {
            if (!timeStr) continue;
            const m = timeStr.match(/^(\d{2}):(\d{2})$/);
            if (!m) continue;
            const boundaryMin = parseInt(m[1], 10) * 60 + parseInt(m[2], 10) + dayOffset * 24 * 60;
            const delta = boundaryMin - currentMin;
            if (delta > 0 && delta < soonestDeltaMin) soonestDeltaMin = delta;
          }
        }
      }
    } catch {
      continue;
    }
  }

  if (!isFinite(soonestDeltaMin)) return MAX_WAIT_MS;
  return Math.min(Math.max(soonestDeltaMin * 60 * 1000, MIN_WAIT_MS), MAX_WAIT_MS);
}

/**
 * Fetches all active stores with coordinates.
 * Priority: primary/first store_addresses coords, fallback to stores.latitude/longitude.
 */
async function fetchStoresWithCoords(): Promise<NearbyStore[]> {
  const [enhancedAddressRows, enhancedStoreRows] = await Promise.all([
    supabase
      .from('store_addresses')
      .select('store_id, latitude, longitude, is_primary, stores!store_addresses_store_id_fkey(id, name, slug, avatar_url, status, schedule, timezone:time_zone)')
      .eq('stores.status', 'active')
      .not('latitude', 'is', null)
      .not('longitude', 'is', null),
    supabase
      .from('stores')
      .select('id, name, slug, avatar_url, latitude, longitude, status, schedule, timezone:time_zone')
      .eq('status', 'active')
      .not('latitude', 'is', null)
      .not('longitude', 'is', null),
  ]);

  const shouldFallbackAddresses = Boolean(enhancedAddressRows.error);
  const shouldFallbackStores = Boolean(enhancedStoreRows.error);

  const [fallbackAddressRows, fallbackStoreRows] = (shouldFallbackAddresses || shouldFallbackStores)
    ? await Promise.all([
        shouldFallbackAddresses
          ? supabase
              .from('store_addresses')
              .select('store_id, latitude, longitude, is_primary, stores!store_addresses_store_id_fkey(id, name, slug, description, avatar_url, status)')
              .eq('stores.status', 'active')
              .not('latitude', 'is', null)
              .not('longitude', 'is', null)
          : Promise.resolve(null),
        shouldFallbackStores
          ? supabase
              .from('stores')
              .select('id, name, slug, description, avatar_url, latitude, longitude, status')
              .eq('status', 'active')
              .not('latitude', 'is', null)
              .not('longitude', 'is', null)
          : Promise.resolve(null),
      ])
    : [null, null];

  const addressRows = shouldFallbackAddresses && fallbackAddressRows
    ? {
        ...fallbackAddressRows,
        data: fallbackAddressRows.data,
        error: fallbackAddressRows.error,
      }
    : enhancedAddressRows;

  const storeRows = shouldFallbackStores && fallbackStoreRows
    ? {
        ...fallbackStoreRows,
        data: fallbackStoreRows.data,
        error: fallbackStoreRows.error,
      }
    : enhancedStoreRows;

  if (addressRows.error && storeRows.error) {
    throw addressRows.error || storeRows.error;
  }

  const byStore = new Map<string, NearbyStore>();

  // Base set from stores table coordinates.
  for (const store of (storeRows.data as any[] | null) || []) {
    byStore.set(store.id, {
      id: store.id,
      name: store.name,
      slug: store.slug,
    description: store.description ?? null,
      avatar_url: store.avatar_url ?? null,
      latitude: store.latitude,
      longitude: store.longitude,
      schedule: normalizeSchedule(store.schedule),
      timezone: store.timezone ?? null,
      schedule_timezone: store.schedule_timezone ?? null,
    });
  }

  // Override with address coordinates (prefer primary when available).
  for (const row of (addressRows.data as any[] | null) || []) {
    const store = row.stores;
    if (!store) continue;
    const existing = byStore.get(store.id);
    const nextSchedule = normalizeSchedule(store.schedule);
    if (!existing || row.is_primary) {
      byStore.set(store.id, {
        id: store.id,
        name: store.name,
        slug: store.slug,
        description: store.description ?? existing?.description ?? null,
        avatar_url: store.avatar_url ?? null,
        latitude: row.latitude,
        longitude: row.longitude,
        schedule: nextSchedule ?? existing?.schedule,
        timezone: store.timezone ?? existing?.timezone ?? null,
        schedule_timezone: store.schedule_timezone ?? existing?.schedule_timezone ?? null,
      });
    }
  }

  return Array.from(byStore.values());
}

function sortByProximity(
  rawStores: NearbyStore[],
  lat: number,
  lon: number
): NearbyStore[] {
  return rawStores
    .map((store) => ({
      ...store,
      distance: haversineKm(lat, lon, store.latitude, store.longitude),
    }))
    .sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
}

export default function NearbyStoresSelector({ selectedStoreId, pinnedStore, onStoreSelect, onDistanceChipClick, onPromoCardSelectionChange, onViewModeChange, onLoadingChange, renderStoreExpansion, renderStoreCatalog, onRentStoreClick, rentStoreLoading = false }: NearbyStoresSelectorProps) {
  const { session, relevantLocation } = useSession();
  const addressManager = useAddressManager();
  const [mode, setMode] = useState<Mode>('landmark');
  const [stores, setStores] = useState<NearbyStore[]>([]);
  const [resolvedPinnedStore, setResolvedPinnedStore] = useState<Partial<NearbyStore> | null>(null);
  const [storeKeywords, setStoreKeywords] = useState<Record<string, string[]>>({});
  const storeKeywordsRef = useRef<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [gpsLocation, setGpsLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [gpsDisplayName, setGpsDisplayName] = useState<string | null>(null);
  const [homeLocation, setHomeLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [landmarkSnapshot, setLandmarkSnapshot] = useState<LandmarkSnapshot | null>(() => deriveLandmarkSnapshot(relevantLocation));
  const [userAddresses, setUserAddresses] = useState<UserAddress[]>([]);
  const [addressesLoaded, setAddressesLoaded] = useState(false);
  const [storeScheduleMap, setStoreScheduleMap] = useState<Record<string, StoreScheduleSnapshot>>({});
  const [storeScheduleValidityMap, setStoreScheduleValidityMap] = useState<Record<string, boolean>>({});
  const [storeOpenMap, setStoreOpenMap] = useState<Record<string, boolean | null>>({});
  const [storeStatusFilter, setStoreStatusFilter] = useState<'todas' | 'abiertas'>('todas');
  const [statusTick, setStatusTick] = useState(() => Date.now());
  const [userMemberStores, setUserMemberStores] = useState<NearbyStore[]>([]);
  const lastSyncedSelectionRef = useRef<string>('');
  const preserveProvidedSelectionOnFirstAutoSelectRef = useRef(Boolean(selectedStoreId));
  const pendingAutoSelectModeRef = useRef<Mode | null>('landmark');
  const initialModeAppliedForUserRef = useRef<string | null>(null);

  useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) {
      setUserMemberStores([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data: memberRows } = await supabase
        .from('store_members')
        .select('store_id')
        .eq('user_id', userId);
      if (cancelled || !memberRows || memberRows.length === 0) return;
      const storeIds = (memberRows as any[]).map((r) => r.store_id);
      const { data: storeRows } = await supabase
        .from('stores')
        .select('id, name, slug, avatar_url, latitude, longitude, status, schedule, timezone:time_zone')
        .in('id', storeIds)
        .eq('status', 'active')
        .not('latitude', 'is', null)
        .not('longitude', 'is', null);
      if (cancelled || !storeRows) return;
      const memberStores: NearbyStore[] = (storeRows as any[]).map((s) => ({
        id: s.id,
        name: s.name,
        slug: s.slug,
        avatar_url: s.avatar_url ?? null,
        latitude: s.latitude,
        longitude: s.longitude,
        schedule: normalizeSchedule(s.schedule),
        timezone: s.timezone ?? null,
        schedule_timezone: null,
      }));
      if (!cancelled) setUserMemberStores(memberStores);
    })();
    return () => { cancelled = true; };
  }, [session?.user?.id]);

  const loadUserAddresses = useCallback(async () => {
    setAddressesLoaded(false);

    if (!session?.user?.id) {
      setUserAddresses([]);
      setAddressesLoaded(true);
      return;
    }

    try {
      const addresses = await getUserAddresses(session.user.id);
      setUserAddresses(addresses || []);
    } catch {
      setUserAddresses([]);
    } finally {
      setAddressesLoaded(true);
    }
  }, [session?.user?.id]);

  useEffect(() => {
    void loadUserAddresses();
  }, [loadUserAddresses, addressManager.isOpen]);

  useEffect(() => {
    const handleAddressUpdated = () => {
      void loadUserAddresses();
    };

    window.addEventListener('address-updated', handleAddressUpdated);
    return () => window.removeEventListener('address-updated', handleAddressUpdated);
  }, [loadUserAddresses]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  useEffect(() => {
    storeKeywordsRef.current = storeKeywords;
  }, [storeKeywords]);

  useEffect(() => {
    if (mode !== 'landmark' && landmarkSnapshot) return;

    const nextSnapshot = deriveLandmarkSnapshot(relevantLocation);
    setLandmarkSnapshot((current) => {
      if (!nextSnapshot) return current;
      if (
        current?.label === nextSnapshot.label &&
        current?.latitude === nextSnapshot.latitude &&
        current?.longitude === nextSnapshot.longitude
      ) {
        return current;
      }
      return nextSnapshot;
    });
  }, [landmarkSnapshot, mode, relevantLocation]);

  const primaryUserAddress = userAddresses.find((address) => address.is_primary) ?? userAddresses[0] ?? null;
  const userCanUseHomeMode = Boolean(session?.user?.id);
  const userHasHomeAddress = Boolean(
    primaryUserAddress?.location?.latitude != null && primaryUserAddress?.location?.longitude != null
  );
  const homeModeSelectionDisabled = userCanUseHomeMode && !userHasHomeAddress;
  const homeButtonLabel = userHasHomeAddress ? 'Casa' : 'Casa: agregar';

  useEffect(() => {
    const currentUserId = session?.user?.id ?? null;
    if (initialModeAppliedForUserRef.current === currentUserId) return;

    if (!currentUserId) {
      pendingAutoSelectModeRef.current = 'landmark';
      setMode('landmark');
      initialModeAppliedForUserRef.current = currentUserId;
      return;
    }

    if (!addressesLoaded) return;

    if (userHasHomeAddress) {
      pendingAutoSelectModeRef.current = 'home';
      setMode('home');
    } else {
      pendingAutoSelectModeRef.current = 'landmark';
      setMode('landmark');
    }

    initialModeAppliedForUserRef.current = currentUserId;
  }, [addressesLoaded, session?.user?.id, userHasHomeAddress]);

  useEffect(() => {
    const hasCityOverride = (() => {
      try {
        return localStorage.getItem('city_selector_override') === '1';
      } catch {
        return false;
      }
    })();

    if (!hasCityOverride) {
      return;
    }

    const nextSnapshot = deriveLandmarkSnapshot(relevantLocation);
    if (nextSnapshot) {
      setLandmarkSnapshot(nextSnapshot);
    }
  }, [relevantLocation]);

  const requestGpsLocation = useCallback(() => {
    setGpsError(null);
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latitude = pos.coords.latitude;
        const longitude = pos.coords.longitude;
        setGpsLocation({ latitude, longitude });

        void (async () => {
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=16&addressdetails=1`,
              {
                mode: 'cors',
                headers: {
                  'User-Agent': 'GoldenLobsterHop/1.0',
                },
              }
            );

            if (!response.ok) return;
            const data = await response.json();
            const display = typeof data?.display_name === 'string' ? data.display_name.trim() : '';
            if (display) {
              const shortLabel = display.split(',').slice(0, 2).join(',').trim();
              setGpsDisplayName(shortLabel || display);
            }
          } catch {
            // Keep generic fallback label when reverse geocoding is unavailable.
          }
        })();

        setLoading(false);
      },
      (err) => {
        setGpsError(
          err.code === 1
            ? 'Permiso de ubicación denegado. Tocá "Mi ubicación" para reintentar.'
            : 'No se pudo obtener tu ubicación'
        );
        setLoading(false);
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  }, []);

  const resolveLandmarkCoords = useCallback((): { latitude: number; longitude: number } | null => {
    if (!landmarkSnapshot) return null;
    return {
      latitude: landmarkSnapshot.latitude,
      longitude: landmarkSnapshot.longitude,
    };
  }, [landmarkSnapshot]);

  const resolveUserHomeCoords = useCallback(async (): Promise<{ latitude: number; longitude: number } | null> => {
    if (primaryUserAddress?.location?.latitude != null && primaryUserAddress.location.longitude != null) {
      return {
        latitude: primaryUserAddress.location.latitude,
        longitude: primaryUserAddress.location.longitude,
      };
    }

    return null;
  }, [primaryUserAddress]);

  // Load stores using landmark city coordinates or saved home coordinates.
  useEffect(() => {
    if (mode === 'gps') return;
    if (session?.user?.id && !addressesLoaded) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const coords = mode === 'landmark'
          ? resolveLandmarkCoords()
          : await resolveUserHomeCoords();
        if (!coords) {
          setHomeLocation(null);
          setStores([]);
          return;
        }
        if (!cancelled) setHomeLocation(coords);
        const raw = await fetchStoresWithCoords();
        if (cancelled) return;
        setStores(sortByProximity(raw, coords.latitude, coords.longitude));
      } catch {
        if (!cancelled) setHomeLocation(null);
        setStores([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [addressesLoaded, mode, resolveLandmarkCoords, resolveUserHomeCoords, session?.user?.id]);

  // Load GPS-based stores
  useEffect(() => {
    if (mode !== 'gps') return;
    if (!gpsLocation) {
      setStores([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const raw = await fetchStoresWithCoords();
        if (cancelled) return;
        setStores(sortByProximity(raw, gpsLocation.latitude, gpsLocation.longitude));
      } catch {
        if (!cancelled) setStores([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode, gpsLocation]);

  const effectivePinnedStore = React.useMemo<NearbyStore | null>(() => {
    const base = (pinnedStore ?? resolvedPinnedStore) as Partial<NearbyStore> | null;
    if (!base?.id || !base.name || !base.slug || base.latitude == null || base.longitude == null) {
      return null;
    }

    const matchedStore = stores.find((store) => store.id === base.id);

    const merged: NearbyStore = {
      id: base.id,
      name: base.name,
      slug: base.slug,
      avatar_url: base.avatar_url ?? null,
      latitude: base.latitude,
      longitude: base.longitude,
      schedule: matchedStore?.schedule ?? base.schedule,
      timezone: matchedStore?.timezone ?? base.timezone ?? null,
      schedule_timezone: matchedStore?.schedule_timezone ?? base.schedule_timezone ?? null,
      ...resolvedPinnedStore,
    };

    if (mode === 'home' && homeLocation) {
      merged.distance = haversineKm(homeLocation.latitude, homeLocation.longitude, merged.latitude, merged.longitude);
    }

    if (mode === 'gps' && gpsLocation) {
      merged.distance = haversineKm(gpsLocation.latitude, gpsLocation.longitude, merged.latitude, merged.longitude);
    }

    return merged;
  }, [pinnedStore, resolvedPinnedStore, mode, gpsLocation, homeLocation, stores]);

  const mergedStores = React.useMemo(() => {
    const base = [...stores];
    // Inject user member stores that aren't already in the location-based list
    const existingIds = new Set(base.map((s) => s.id));
    const memberExtras = userMemberStores.filter((s) => !existingIds.has(s.id));
    const allStores = [...base, ...memberExtras];

    const prioritized = (() => {
      if (!effectivePinnedStore?.id) return allStores;
      const withoutPinned = allStores.filter((store) => store.id !== effectivePinnedStore.id);
      const existingPinned = allStores.find((store) => store.id === effectivePinnedStore.id);
      return [{ ...effectivePinnedStore, ...existingPinned }, ...withoutPinned];
    })();

    // Defensive de-duplication by id so carousel neighbors and keyboard navigation
    // always move to a different store.
    const seen = new Set<string>();
    const unique: NearbyStore[] = [];
    for (const store of prioritized) {
      if (!store?.id || seen.has(store.id)) continue;
      seen.add(store.id);
      unique.push(store);
    }

    return unique;
  }, [stores, effectivePinnedStore, userMemberStores]);

  const mergedStoresIdsKey = React.useMemo(
    () => mergedStores.map((s) => s.id).sort().join(','),
    [mergedStores]
  );

  const fetchStoreSchedulesSnapshot = useCallback(async (storeIds: string[]) => {
    if (storeIds.length === 0) {
      return { data: [], error: null };
    }

    return await supabase
      .from('stores')
      .select('id, schedule, timezone:time_zone')
      .in('id', storeIds);
  }, []);

  useEffect(() => {
    if (mergedStores.length === 0) {
      setStoreScheduleMap({});
      return;
    }

    // Seed the map from already-available schedule data on the store objects,
    // so cards show the correct status even before the secondary query completes.
    const seedMap: Record<string, StoreScheduleSnapshot> = {};
    for (const store of mergedStores) {
      const normalized = normalizeSchedule(store.schedule);
      if (normalized) {
        seedMap[store.id] = {
          schedule: normalized,
          timezone: store.timezone ?? null,
          schedule_timezone: store.schedule_timezone ?? null,
        };
      }
    }
    if (Object.keys(seedMap).length > 0) {
      setStoreScheduleMap((prev) => ({ ...seedMap, ...prev }));
    }

    let cancelled = false;

    const loadStoreSchedules = async () => {
      const storeIds = Array.from(new Set(mergedStores.map((store) => store.id)));
      const { data, error } = await fetchStoreSchedulesSnapshot(storeIds);

      if (cancelled) return;
      if (error || !data) return;

      const nextMap: Record<string, StoreScheduleSnapshot> = {};
      for (const row of data as Array<{ id: string; schedule: any; timezone: string | null; schedule_timezone: string | null }>) {
        nextMap[row.id] = {
          schedule: normalizeSchedule(row.schedule),
          timezone: row.timezone ?? null,
          schedule_timezone: row.schedule_timezone ?? null,
        };
      }

      if (!cancelled) {
        setStoreScheduleMap(nextMap);
      }
    };

    void loadStoreSchedules();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchStoreSchedulesSnapshot, mergedStoresIdsKey]);

  const getEffectiveStoreScheduleData = useCallback((store: NearbyStore) => {
    const override = storeScheduleMap[store.id];
    return {
      ...store,
      schedule: override?.schedule ?? normalizeSchedule(store.schedule),
      timezone: override?.timezone ?? store.timezone ?? null,
      schedule_timezone: override?.schedule_timezone ?? store.schedule_timezone ?? null,
    };
  }, [storeScheduleMap]);

  useEffect(() => {
    const effectiveStores = mergedStores.map(getEffectiveStoreScheduleData);
    const delay = msUntilNextBoundary(effectiveStores);
    const timeoutId = window.setTimeout(() => setStatusTick(Date.now()), delay);
    return () => window.clearTimeout(timeoutId);
  }, [getEffectiveStoreScheduleData, mergedStores, statusTick]);

  useEffect(() => {
    if (mergedStores.length === 0) {
      setStoreOpenMap({});
      setStoreScheduleValidityMap({});
      return;
    }

    const statusMap: Record<string, boolean | null> = {};
    const validityMap: Record<string, boolean> = {};
    for (const store of mergedStores) {
      const effectiveStore = getEffectiveStoreScheduleData(store);
      const normalizedSchedule = normalizeSchedule(effectiveStore.schedule);
      const hasSchedule = Array.isArray(normalizedSchedule) || Boolean(normalizedSchedule && Array.isArray((normalizedSchedule as any).week));
      validityMap[store.id] = hasSchedule;
      statusMap[store.id] = hasSchedule
        ? isStoreOpenNow({
            ...effectiveStore,
            schedule: normalizedSchedule,
          })
        : null;
    }
    setStoreOpenMap(statusMap);
    setStoreScheduleValidityMap(validityMap);
  }, [getEffectiveStoreScheduleData, mergedStores, statusTick]);

  const userMemberStoreIds = React.useMemo(
    () => new Set(userMemberStores.map((s) => s.id)),
    [userMemberStores]
  );

  const displayStores = React.useMemo(() => {
    const pinnedId = effectivePinnedStore?.id ?? pinnedStore?.id ?? null;
    return [...mergedStores].sort((a, b) => {
      // User's member stores appear first (leftmost in carousel)
      const aIsMember = userMemberStoreIds.has(a.id);
      const bIsMember = userMemberStoreIds.has(b.id);
      if (aIsMember && !bIsMember) return -1;
      if (bIsMember && !aIsMember) return 1;

      // Pinned store comes right after member stores
      if (pinnedId) {
        if (a.id === pinnedId && b.id !== pinnedId) return -1;
        if (b.id === pinnedId && a.id !== pinnedId) return 1;
      }

      const aOpen = storeOpenMap[a.id];
      const bOpen = storeOpenMap[b.id];
      if (aOpen === true && bOpen !== true) return -1;
      if (bOpen === true && aOpen !== true) return 1;
      return (a.distance ?? Infinity) - (b.distance ?? Infinity);
    });
  }, [effectivePinnedStore?.id, mergedStores, pinnedStore?.id, storeOpenMap, userMemberStoreIds]);

  const filteredDisplayStores = React.useMemo(() => {
    if (storeStatusFilter === 'todas') {
      return displayStores;
    }

    if (storeStatusFilter === 'abiertas') {
      const hasKnownOpenState = displayStores.some((store) => storeOpenMap[store.id] === true || storeOpenMap[store.id] === false);
      if (!hasKnownOpenState) {
        return displayStores;
      }
      return displayStores.filter((store) => storeOpenMap[store.id] === true);
    }

    return displayStores;
  }, [displayStores, storeStatusFilter, storeOpenMap]);

  const handleOpenFilterClick = useCallback(() => {
    setStoreStatusFilter('abiertas');

    const storeIds = Array.from(new Set(mergedStores.map((store) => store.id)));

    void (async () => {
      const { data, error } = await fetchStoreSchedulesSnapshot(storeIds);
      const rows = (data ?? []) as Array<{ id: string; schedule: any; timezone: string | null; schedule_timezone: string | null }>;

      const derivedStatusFromFetch = rows.map((row) => {
        const normalizedSchedule = normalizeSchedule(row.schedule);
        const hasSchedule = Array.isArray(normalizedSchedule) || Boolean(normalizedSchedule && Array.isArray((normalizedSchedule as any).week));
        const openState = hasSchedule
          ? isStoreOpenNow({
              id: row.id,
              schedule: normalizedSchedule,
              timezone: row.timezone ?? null,
              schedule_timezone: row.schedule_timezone ?? null,
            })
          : null;

        return {
          id: row.id,
          hasSchedule,
          openState,
          timezone: row.timezone ?? null,
          schedule_timezone: row.schedule_timezone ?? null,
          scheduleRaw: row.schedule,
          scheduleNormalized: normalizedSchedule,
        };
      });

      const filteredByCurrentMap = displayStores
        .filter((store) => storeOpenMap[store.id] === true)
        .map((store) => ({
          id: store.id,
          name: store.name,
          openState: storeOpenMap[store.id],
        }));

      const errorSummary = error
        ? {
            code: (error as any)?.code ?? null,
            message: (error as any)?.message ?? null,
            details: (error as any)?.details ?? null,
            hint: (error as any)?.hint ?? null,
          }
        : null;

      console.groupCollapsed('[NearbyStoresSelector] debug filtro abiertas');
      console.log('storeIds requested:', storeIds);
      console.log('fetch response:', { data, error });
      console.log('fetch error summary:', errorSummary);
      console.log('derivedStatusFromFetch:', derivedStatusFromFetch);
      console.log('storeOpenMap current:', storeOpenMap);
      console.log('displayStores current:', displayStores.map((store) => ({ id: store.id, name: store.name, openState: storeOpenMap[store.id] })));
      console.log('filtered result by current map:', filteredByCurrentMap);
      console.groupEnd();
    })();
  }, [displayStores, fetchStoreSchedulesSnapshot, mergedStores, storeOpenMap]);

  useEffect(() => {
    if (loading || pendingAutoSelectModeRef.current !== mode || displayStores.length === 0 || !onStoreSelect) return;

    if (preserveProvidedSelectionOnFirstAutoSelectRef.current && selectedStoreId) {
      preserveProvidedSelectionOnFirstAutoSelectRef.current = false;
      pendingAutoSelectModeRef.current = null;

      const providedStore = displayStores.find((store) => store.id === selectedStoreId);
      if (providedStore) {
        lastSyncedSelectionRef.current = '';
        onStoreSelect(providedStore, { triggerAutoScroll: false });
      }
      return;
    }

    pendingAutoSelectModeRef.current = null;
    lastSyncedSelectionRef.current = '';
    onStoreSelect(displayStores[0]);
  }, [displayStores, loading, mode, onStoreSelect, selectedStoreId]);

  useEffect(() => {
    let cancelled = false;

    const fetchKeywordsForStores = async (storeIds: string[]): Promise<Record<string, string[]>> => {
      if (storeIds.length === 0) return {};

      const { data, error } = await supabase
        .from('products')
        .select('store_id, title')
        .in('store_id', storeIds)
        .eq('status', 'available');

      if (error || !data) {
        return {};
      }

      const groupedByStore: Record<string, string[]> = {};
      for (const row of data as Array<{ store_id: string; title: string }>) {
        if (!row?.store_id || !row?.title) continue;
        if (!groupedByStore[row.store_id]) groupedByStore[row.store_id] = [];
        groupedByStore[row.store_id].push(row.title);
      }

      const keywordsByStore: Record<string, string[]> = {};
      for (const storeId of storeIds) {
        const titles = groupedByStore[storeId] || [];
        keywordsByStore[storeId] = extractKeywordsFromTitles(titles, 4);
      }

      return keywordsByStore;
    };

    const loadKeywords = async () => {
      if (displayStores.length === 0) {
        setStoreKeywords({});
        return;
      }

      const storeIds = Array.from(new Set(displayStores.map((s) => s.id)));
      const storeById = new Map(displayStores.map((store) => [store.id, store] as const));

      setStoreKeywords((previous) => {
        const next: Record<string, string[]> = {};
        for (const storeId of storeIds) {
          if (previous[storeId]) next[storeId] = previous[storeId];
        }
        return next;
      });

      const missingStoreIds = storeIds.filter((storeId) => {
        const current = storeKeywordsRef.current[storeId];
        return !Array.isArray(current) || current.length === 0;
      });
      if (missingStoreIds.length === 0) return;

      const priorityStoreIds: string[] = [];
      if (selectedStoreId && missingStoreIds.includes(selectedStoreId)) {
        priorityStoreIds.push(selectedStoreId);
      }
      for (const id of missingStoreIds) {
        if (priorityStoreIds.includes(id)) continue;
        priorityStoreIds.push(id);
        if (priorityStoreIds.length >= INITIAL_KEYWORD_BATCH_SIZE) break;
      }

      const firstBatch = await fetchKeywordsForStores(priorityStoreIds);
      if (cancelled) return;

      const firstBatchWithFallback: Record<string, string[]> = { ...firstBatch };
      for (const storeId of priorityStoreIds) {
        const current = firstBatchWithFallback[storeId] || [];
        if (current.length > 0) continue;
        const fallbackSource = storeById.get(storeId);
        firstBatchWithFallback[storeId] = mergeKeywordsWithDescription(current, fallbackSource);
      }

      setStoreKeywords((previous) => ({ ...previous, ...firstBatchWithFallback }));

      const remainingStoreIds = missingStoreIds.filter((storeId) => !priorityStoreIds.includes(storeId));
      if (remainingStoreIds.length === 0) return;

      window.setTimeout(async () => {
        if (cancelled) return;
        const deferredBatch = await fetchKeywordsForStores(remainingStoreIds);
        if (cancelled) return;
        const deferredBatchWithFallback: Record<string, string[]> = { ...deferredBatch };
        for (const storeId of remainingStoreIds) {
          const current = deferredBatchWithFallback[storeId] || [];
          if (current.length > 0) continue;
          const fallbackSource = storeById.get(storeId);
          deferredBatchWithFallback[storeId] = mergeKeywordsWithDescription(current, fallbackSource);
        }
        setStoreKeywords((previous) => ({ ...previous, ...deferredBatchWithFallback }));
      }, 250);
    };

    void loadKeywords();

    return () => {
      cancelled = true;
    };
  }, [displayStores, selectedStoreId]);

  const handleGpsMode = () => {
    pendingAutoSelectModeRef.current = 'gps';
    setHasManualStoreSelection(false);
    setMode('gps');
    // Always try again on click so users can re-grant permission from browser prompt/settings.
    requestGpsLocation();
  };

  const handleLandmarkMode = () => {
    pendingAutoSelectModeRef.current = 'landmark';
    setHasManualStoreSelection(false);
    const nextSnapshot = deriveLandmarkSnapshot(relevantLocation);
    if (nextSnapshot) setLandmarkSnapshot(nextSnapshot);
    setMode('landmark');
    setGpsError(null);
  };

  const handleHomeMode = () => {
    setGpsError(null);
    if (!userCanUseHomeMode) return;
    if (!userHasHomeAddress) {
      addressManager.open({ forceOpen: true });
      return;
    }

    pendingAutoSelectModeRef.current = 'home';
    setHasManualStoreSelection(false);
    setMode('home');
  };

  const showEmpty = !loading && stores.length === 0 && !gpsError;

  const homeLandmarkLabel = React.useMemo(() => {
    return landmarkSnapshot?.landmarkName || 'Plaza 9 de Julio';
  }, [landmarkSnapshot]);

  const gpsCapturedLabel = React.useMemo(() => {
    if (!gpsLocation) return 'Mi ubicación';
    return gpsDisplayName || 'Mi ubicación';
  }, [gpsDisplayName, gpsLocation]);
  const isShowingResolvedGpsText = mode === 'gps' && Boolean(gpsDisplayName && gpsDisplayName.trim());
  const GpsButtonIcon = isShowingResolvedGpsText ? RotateCw : LocateFixed;

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const suppressCardClickRef = React.useRef(false);
  const dragResetTimeoutRef = React.useRef<number | null>(null);
  const carouselSyncFrameRef = React.useRef<number | null>(null);
  const pointerDragStateRef = React.useRef({
    active: false,
    pointerId: -1,
    startX: 0,
    startY: 0,
  });
  const carouselCardRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const promoDetailsRef = React.useRef<HTMLDivElement | null>(null);
  const [hasCarouselOverflow, setHasCarouselOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [hoveredCardKey, setHoveredCardKey] = useState<string | null>(null);
  const [promoCardSelected, setPromoCardSelected] = useState(false);
  const [viewMode, setViewMode] = useState<SelectorViewMode>('carousel');
  const [hasManualStoreSelection, setHasManualStoreSelection] = useState(false);
  const [directoryColCount, setDirectoryColCount] = useState(1);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [catalogOpenStoreId, setCatalogOpenStoreId] = useState<string | null>(null);
  const controlGroupClassName = 'inline-flex items-center rounded-full border border-border bg-background p-1 shadow-sm';
  const desktopControlButtonClassName = 'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap';
  const mobileControlButtonClassName = 'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap';
  const viewButtonDesktopClassName = 'px-3 py-1.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap';
  const viewButtonMobileClassName = 'px-2.5 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap';
  const chevronButtonClassName = 'inline-flex items-center justify-center h-8 w-8 rounded-full border border-white/35 bg-white/20 text-white backdrop-blur-xl transition-all supports-[backdrop-filter]:bg-white/15 dark:border-white/20 dark:bg-white/10 disabled:opacity-45 disabled:cursor-not-allowed disabled:active:scale-100';

  const renderChevronButton = (direction: 'left' | 'right', enabled: boolean, hideWhenDisabled = false) => {
    const isLeft = direction === 'left';
    const label = isLeft ? 'Retroceder galería' : 'Avanzar galería';
    const icon = isLeft ? <ChevronLeft className="block h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="block h-3.5 w-3.5 shrink-0" />;
    const iconOffsetClassName = isLeft ? '-translate-x-[5px]' : 'translate-x-[5px]';

    if (hideWhenDisabled && !enabled) {
      return null;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <button
              type="button"
              onClick={enabled ? () => scrollByCard(direction) : undefined}
              disabled={!enabled}
              aria-disabled={!enabled}
              className={cn(chevronButtonClassName, 'leading-none', enabled && 'active:scale-[0.97]')}
              aria-label={isLeft ? 'Anterior' : 'Siguiente'}
            >
              <motion.span
                aria-hidden="true"
                animate={{ scale: [1, 1.12, 1] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                className={cn('flex h-3.5 w-3.5 items-center justify-center leading-none', iconOffsetClassName)}
              >
                {icon}
              </motion.span>
            </button>
          </span>
        </TooltipTrigger>
        <TooltipContent variant="navbar">{label}</TooltipContent>
      </Tooltip>
    );
  };

  const renderPromoRentButton = (size: 'compact' | 'expanded') => {
    const isCompact = size === 'compact';
    const disabled = !onRentStoreClick || rentStoreLoading;

    return (
      <span
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        title={rentStoreLoading ? 'Agregando...' : 'Agregar alquiler de tienda'}
        onClick={(event) => {
          event.stopPropagation();
          if (disabled) return;
          void onRentStoreClick?.();
        }}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            void onRentStoreClick?.();
          }
        }}
        className={cn(
          'rounded-full shadow-lg hover:shadow-xl transition-all active:scale-90 duration-75 inline-flex items-center gap-1.5 justify-center bg-primary text-primary-foreground',
          isCompact
            ? 'h-9 px-2.5 text-[10px] font-semibold whitespace-nowrap'
            : 'h-[43px] px-3 text-xs font-semibold',
          disabled ? 'opacity-60 cursor-not-allowed pointer-events-none' : 'cursor-pointer'
        )}
      >
        <Plus className={isCompact ? 'h-3.5 w-3.5' : 'h-6 w-6'} />
        <Store className={isCompact ? 'h-3.5 w-3.5' : 'h-5 w-5'} />
      </span>
    );
  };
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      setDirectoryColCount(w >= 1280 ? 3 : w >= 640 ? 2 : 1);
      setIsMobileViewport(w < 768);
    };

    update();
    window.addEventListener('resize', update, { passive: true });
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    if (!promoCardSelected || viewMode !== 'carousel') return;

    let rafId1: number | null = null;
    let rafId2: number | null = null;
    const timeoutId = window.setTimeout(() => {
      const panel = promoDetailsRef.current;
      if (!panel) return;

      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      if (!isMobile) {
        panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      window.dispatchEvent(new CustomEvent('app-header-visibility', { detail: { hidden: true } }));

      // Run after header hidden state is applied to keep a single smooth movement.
      rafId1 = window.requestAnimationFrame(() => {
        rafId2 = window.requestAnimationFrame(() => {
          const currentPanel = promoDetailsRef.current;
          if (!currentPanel) return;

          const topOffset = 8;
          const panelTop = window.scrollY + currentPanel.getBoundingClientRect().top;
          const targetTop = Math.max(0, panelTop - topOffset);
          window.scrollTo({ top: targetTop, behavior: 'smooth' });
        });
      });
    }, 120);

    return () => {
      window.clearTimeout(timeoutId);
      if (rafId1 !== null) window.cancelAnimationFrame(rafId1);
      if (rafId2 !== null) window.cancelAnimationFrame(rafId2);
    };
  }, [promoCardSelected, viewMode]);

  useEffect(() => {
    onPromoCardSelectionChange?.(promoCardSelected);
  }, [onPromoCardSelectionChange, promoCardSelected]);

  const handleStoreSelect = useCallback((store: NearbyStore) => {
    setPromoCardSelected(false);
    setHasManualStoreSelection(true);
    onStoreSelect?.(store, { triggerAutoScroll: true });
  }, [onStoreSelect]);

  const handleDistanceChipClick = useCallback((store: NearbyStore) => {
    setPromoCardSelected(false);
    setHasManualStoreSelection(true);
    onStoreSelect?.(store, { triggerAutoScroll: true });
    onDistanceChipClick?.(store);
  }, [onDistanceChipClick, onStoreSelect]);

  const handlePromoCardToggle = useCallback(() => {
    setPromoCardSelected((previous) => {
      const next = !previous;
      if (next) {
        // When promo is active, visually deselect store cards and hide directory expansion state.
        setHasManualStoreSelection(false);
        setCatalogOpenStoreId(null);
      }
      return next;
    });
  }, []);

  const markDragClickSuppressed = useCallback(() => {
    suppressCardClickRef.current = true;
    if (dragResetTimeoutRef.current !== null) {
      window.clearTimeout(dragResetTimeoutRef.current);
    }
    dragResetTimeoutRef.current = window.setTimeout(() => {
      suppressCardClickRef.current = false;
      dragResetTimeoutRef.current = null;
    }, 140);
  }, []);

  const handleCarouselPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    pointerDragStateRef.current = {
      active: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };
  }, []);

  const handleCarouselPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const state = pointerDragStateRef.current;
    if (!state.active || state.pointerId !== event.pointerId) return;

    const deltaX = Math.abs(event.clientX - state.startX);
    const deltaY = Math.abs(event.clientY - state.startY);
    if (deltaX > 8 || deltaY > 8) {
      markDragClickSuppressed();
    }
  }, [markDragClickSuppressed]);

  const handleCarouselPointerEnd = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const state = pointerDragStateRef.current;
    if (!state.active || state.pointerId !== event.pointerId) return;
    pointerDragStateRef.current.active = false;
  }, []);

  const syncHoveredCardFromPoint = useCallback((clientX: number, clientY: number) => {
    const pointedElement = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
    const hoveredButton = pointedElement?.closest<HTMLButtonElement>('button[data-card-hover-key]');
    const nextHoverKey = hoveredButton?.dataset.cardHoverKey ?? null;

    setHoveredCardKey((current) => (current === nextHoverKey ? current : nextHoverKey));
  }, []);

  const alphabetDirectory = React.useMemo(() => {
    const groups = new Map<string, NearbyStore[]>();
    for (const store of filteredDisplayStores) {
      const first = (store.name || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .charAt(0)
        .toUpperCase();
      const letter = /^[A-Z]$/.test(first) ? first : '#';
      if (!groups.has(letter)) groups.set(letter, []);
      groups.get(letter)!.push(store);
    }

    return Array.from(groups.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([letter, list]) => ({
        letter,
        stores: list.sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }, [filteredDisplayStores]);

  const virtualCarouselStores = React.useMemo(() => {
    const total = filteredDisplayStores.length;
    if (total === 0) return [] as Array<{ store: NearbyStore; sourceIndex: number; virtualIndex: number }>;
    const virtual: Array<{ store: NearbyStore; sourceIndex: number; virtualIndex: number }> = [];

    for (let i = 0; i < total; i += 1) {
      virtual.push({
        store: filteredDisplayStores[i],
        sourceIndex: i,
        virtualIndex: virtual.length,
      });
    }

    return virtual;
  }, [filteredDisplayStores]);

  const promoVirtualIndex = React.useMemo(() => {
    if (filteredDisplayStores.length === 0) return -1;
    return virtualCarouselStores.length;
  }, [filteredDisplayStores.length, virtualCarouselStores.length]);

  const carouselItemCount = React.useMemo(
    () => virtualCarouselStores.length + (filteredDisplayStores.length > 0 ? 1 : 0),
    [filteredDisplayStores.length, virtualCarouselStores.length]
  );
  const maxCarouselIndex = Math.max(0, carouselItemCount - 1);

  useEffect(() => {
    setCurrentCarouselIndex((prev) => Math.max(0, Math.min(prev, maxCarouselIndex)));
  }, [maxCarouselIndex]);

  const updateScrollFades = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      setHasCarouselOverflow(false);
      setCanScrollLeft(false);
      setCanScrollRight(false);
      setCurrentCarouselIndex(0);
      return;
    }

    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    setHasCarouselOverflow(maxScrollLeft > 2);

    const SCROLL_BOUND_START_EPSILON = 2;
    const START_EPSILON = 18;
    const END_EPSILON = 6;
    const currentScrollLeft = Math.max(0, Math.min(container.scrollLeft, maxScrollLeft));
    const atStart = currentScrollLeft <= SCROLL_BOUND_START_EPSILON;
    const atEnd = maxScrollLeft <= END_EPSILON || currentScrollLeft >= maxScrollLeft - END_EPSILON;

    if (atStart) {
      setCanScrollLeft(false);
      setCanScrollRight(maxScrollLeft > END_EPSILON);
      setCurrentCarouselIndex(0);
      return;
    }

    if (atEnd) {
      setCanScrollLeft(true);
      setCanScrollRight(false);
      setCurrentCarouselIndex(maxCarouselIndex);
      return;
    }

    const totalVirtualCards = carouselItemCount;
    const availableIndexes: number[] = [];
    for (let i = 0; i < totalVirtualCards; i += 1) {
      if (carouselCardRefs.current[i]) availableIndexes.push(i);
    }
    if (availableIndexes.length === 0) {
      // Fallback: before refs hydrate, approximate index from native bounds.
      setCanScrollLeft(!atStart);
      setCanScrollRight(!atEnd);
      if (atStart) {
        setCurrentCarouselIndex(0);
      } else if (atEnd) {
        setCurrentCarouselIndex(maxCarouselIndex);
      }
      return;
    }

    const firstIndex = availableIndexes[0];
    const lastIndex = availableIndexes[availableIndexes.length - 1];

    const horizontalPadding = 8;
    const getTargetScrollLeft = (index: number) => {
      const card = carouselCardRefs.current[index];
      if (!card) return currentScrollLeft;
      return Math.max(0, Math.min(card.offsetLeft - horizontalPadding, maxScrollLeft));
    };

    let anchorIndex = firstIndex;
    let bestAnchorOffset = Number.NEGATIVE_INFINITY;
    for (const idx of availableIndexes) {
      const card = carouselCardRefs.current[idx];
      if (!card) continue;
      const anchorLeft = Math.max(card.offsetLeft - horizontalPadding, 0);
      if (anchorLeft <= currentScrollLeft + START_EPSILON && anchorLeft > bestAnchorOffset) {
        bestAnchorOffset = anchorLeft;
        anchorIndex = idx;
      }
    }

    const clampedAnchorIndex = Math.max(firstIndex, Math.min(anchorIndex, lastIndex));

    setCanScrollLeft(!atStart);
    setCanScrollRight(!atEnd);
    setCurrentCarouselIndex(clampedAnchorIndex);
  }, [carouselItemCount, maxCarouselIndex]);

  const scheduleCarouselSync = useCallback(() => {
    if (carouselSyncFrameRef.current !== null) {
      window.cancelAnimationFrame(carouselSyncFrameRef.current);
    }

    carouselSyncFrameRef.current = window.requestAnimationFrame(() => {
      carouselSyncFrameRef.current = null;
      updateScrollFades();
    });
  }, [updateScrollFades]);

  const syncCarouselUntilSettled = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      scheduleCarouselSync();
      return;
    }

    if (carouselSyncFrameRef.current !== null) {
      window.cancelAnimationFrame(carouselSyncFrameRef.current);
    }

    let lastLeft = container.scrollLeft;
    let stableFrames = 0;

    const tick = () => {
      const nextContainer = scrollContainerRef.current;
      if (!nextContainer) {
        carouselSyncFrameRef.current = null;
        return;
      }

      updateScrollFades();

      const nextLeft = nextContainer.scrollLeft;
      if (Math.abs(nextLeft - lastLeft) < 0.5) {
        stableFrames += 1;
      } else {
        stableFrames = 0;
      }

      lastLeft = nextLeft;

      if (stableFrames >= 4) {
        carouselSyncFrameRef.current = null;
        return;
      }

      carouselSyncFrameRef.current = window.requestAnimationFrame(tick);
    };

    carouselSyncFrameRef.current = window.requestAnimationFrame(tick);
  }, [scheduleCarouselSync, updateScrollFades]);

  const scrollCardToLeftEdge = useCallback((container: HTMLDivElement, card: HTMLElement, behavior: ScrollBehavior = 'smooth') => {
    const horizontalPadding = 8;
    const targetScrollLeft = card.offsetLeft - horizontalPadding;
    const maxScrollLeft = Math.max(container.scrollWidth - container.clientWidth, 0);
    const clampedTarget = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

    container.scrollTo({ left: clampedTarget, behavior });
  }, []);

  const getLeadingVirtualIndex = useCallback((container: HTMLDivElement) => {
    const containerRect = container.getBoundingClientRect();
    const currentLeftX = containerRect.left;

    let leadingIndex = -1;
    let minPositiveDistance = Number.POSITIVE_INFINITY;
    let fallbackIndex = -1;
    let fallbackDistance = Number.POSITIVE_INFINITY;

    for (let i = 0; i < carouselCardRefs.current.length; i += 1) {
      const card = carouselCardRefs.current[i];
      if (!card) continue;
      const rect = card.getBoundingClientRect();
      const distanceFromLeft = rect.left - currentLeftX;

      if (distanceFromLeft >= -8 && distanceFromLeft < minPositiveDistance) {
        minPositiveDistance = distanceFromLeft;
        leadingIndex = i;
      }

      const absDistance = Math.abs(distanceFromLeft);
      if (absDistance < fallbackDistance) {
        fallbackDistance = absDistance;
        fallbackIndex = i;
      }
    }

    return leadingIndex >= 0 ? leadingIndex : fallbackIndex;
  }, []);

  const scrollByCard = useCallback((direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const totalVirtualCards = carouselItemCount;
    if (totalVirtualCards === 0) return;

    const availableIndexes: number[] = [];
    for (let i = 0; i < totalVirtualCards; i += 1) {
      if (carouselCardRefs.current[i]) availableIndexes.push(i);
    }
    if (availableIndexes.length === 0) return;

    const firstIndex = availableIndexes[0];
    const lastIndex = availableIndexes[availableIndexes.length - 1];
    const horizontalPadding = 8;
    const currentLeft = container.scrollLeft;

    let anchorIndex = firstIndex;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const idx of availableIndexes) {
      const card = carouselCardRefs.current[idx];
      if (!card) continue;
      const anchorLeft = Math.max(card.offsetLeft - horizontalPadding, 0);
      const distance = Math.abs(anchorLeft - currentLeft);
      if (distance < bestDistance) {
        bestDistance = distance;
        anchorIndex = idx;
      }
    }

    const currentIndex = Math.max(firstIndex, Math.min(anchorIndex, lastIndex));
    const rawTargetIndex = direction === 'right' ? currentIndex + 1 : currentIndex - 1;
    const targetIndex = Math.max(firstIndex, Math.min(rawTargetIndex, lastIndex));
    if (targetIndex === currentIndex) {
      updateScrollFades();
      return;
    }
    const targetCard = carouselCardRefs.current[targetIndex];

    if (targetCard) {
      scrollCardToLeftEdge(container, targetCard);
    } else {
      const cardStep = Math.max(container.clientWidth * 0.7, 180);
      const delta = direction === 'right' ? cardStep : -cardStep;
      container.scrollBy({ left: delta, behavior: 'smooth' });
    }

    syncCarouselUntilSettled();
  }, [carouselItemCount, scrollCardToLeftEdge, syncCarouselUntilSettled, updateScrollFades]);

  useEffect(() => {
    return () => {
      if (carouselSyncFrameRef.current !== null) {
        window.cancelAnimationFrame(carouselSyncFrameRef.current);
      }
      if (dragResetTimeoutRef.current !== null) {
        window.clearTimeout(dragResetTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (promoCardSelected || !selectedStoreId || filteredDisplayStores.length === 0) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const currentIndex = filteredDisplayStores.findIndex((s) => s.id === selectedStoreId);
      if (currentIndex === -1) return;
      let nextIndex = currentIndex;
      if (e.key === 'ArrowRight') {
        for (let i = currentIndex + 1; i < filteredDisplayStores.length; i += 1) {
          if (filteredDisplayStores[i].id !== selectedStoreId) {
            nextIndex = i;
            break;
          }
        }
      } else {
        for (let i = currentIndex - 1; i >= 0; i -= 1) {
          if (filteredDisplayStores[i].id !== selectedStoreId) {
            nextIndex = i;
            break;
          }
        }
      }
      if (nextIndex === currentIndex) return;
      e.preventDefault();
      const nextStore = filteredDisplayStores[nextIndex];
      handleStoreSelect(nextStore);

      const container = scrollContainerRef.current;
      if (!container) return;

      const matchingIndexes: number[] = [];
      for (let i = 0; i < virtualCarouselStores.length; i += 1) {
        if (virtualCarouselStores[i].store.id === nextStore.id) matchingIndexes.push(i);
      }
      if (matchingIndexes.length === 0) return;

      const leadingIndex = getLeadingVirtualIndex(container);
      const fallback = matchingIndexes[Math.floor(matchingIndexes.length / 2)] ?? matchingIndexes[0];
      const targetIndex = leadingIndex < 0
        ? fallback
        : matchingIndexes.reduce((closest, idx) => (
          Math.abs(idx - leadingIndex) < Math.abs(closest - leadingIndex) ? idx : closest
        ), fallback);

      const targetCard = carouselCardRefs.current[targetIndex];
      if (!targetCard) return;
      scrollCardToLeftEdge(container, targetCard);

    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filteredDisplayStores, filteredDisplayStores.length, getLeadingVirtualIndex, handleStoreSelect, promoCardSelected, scrollCardToLeftEdge, selectedStoreId, virtualCarouselStores]);

  useEffect(() => {
    if (loading || mode !== 'gps' || stores.length > 0 || !effectivePinnedStore?.id) return;
    if (selectedStoreId === effectivePinnedStore.id) return;

    onStoreSelect?.(effectivePinnedStore, { triggerAutoScroll: false });
  }, [loading, mode, stores, effectivePinnedStore, onStoreSelect, selectedStoreId]);

  useEffect(() => {
    if (!selectedStoreId || displayStores.length === 0 || !onStoreSelect) return;

    const selected = displayStores.find((store) => store.id === selectedStoreId);
    if (!selected) return;

    const signature = `${selected.id}:${selected.distance ?? 'na'}:${selected.latitude}:${selected.longitude}:${mode}`;
    if (lastSyncedSelectionRef.current === signature) return;

    lastSyncedSelectionRef.current = signature;
    onStoreSelect(selected, { triggerAutoScroll: false });
  }, [selectedStoreId, displayStores, onStoreSelect, mode]);

  useEffect(() => {
    if (!selectedStoreId) {
      setHasManualStoreSelection(false);
      return;
    }

    if (pinnedStore?.id && selectedStoreId !== pinnedStore.id) {
      setHasManualStoreSelection(true);
    }
  }, [selectedStoreId, pinnedStore?.id]);

  useEffect(() => {
    if (viewMode !== 'carousel') {
      setHasCarouselOverflow(false);
      return;
    }
    updateScrollFades();
  }, [displayStores, viewMode, updateScrollFades]);

  useEffect(() => {
    // Auto-scroll selected store into view when viewMode is 'carousel'
    if (viewMode !== 'carousel' || promoCardSelected || !selectedStoreId) return;
    if (!hasManualStoreSelection) {
      updateScrollFades();
      return;
    }

    const container = scrollContainerRef.current;
    if (!container) return;

    const baseIndex = displayStores.findIndex((store) => store.id === selectedStoreId);
    if (baseIndex < 0) return;

    const targetIndex = baseIndex;
    const btn = carouselCardRefs.current[targetIndex];
    if (!btn) return;

    // Use setTimeout to ensure DOM has settled
    const timeoutId = setTimeout(() => {
      scrollCardToLeftEdge(container, btn, 'auto');
      updateScrollFades();
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [displayStores, displayStores.length, hasManualStoreSelection, promoCardSelected, scrollCardToLeftEdge, selectedStoreId, viewMode, updateScrollFades]);

  useEffect(() => {
    if (viewMode !== 'carousel') return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => scheduleCarouselSync();
    const handleResize = () => scheduleCarouselSync();

    container.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [scheduleCarouselSync, viewMode]);

  return (
    <div className="mb-5 mt-0 md:mt-0.5">
      {!loading && (
      <div className={cn('hidden md:items-center md:justify-between md:gap-4 mb-4', viewMode !== 'carousel' && 'md:flex')}>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={controlGroupClassName}>
            <button
              onClick={handleLandmarkMode}
              className={cn(desktopControlButtonClassName, mode === 'landmark' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground')}
            >
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="max-w-[13rem] truncate">
                {homeLandmarkLabel}
              </span>
            </button>
            {userCanUseHomeMode && (
              <button
                onClick={handleHomeMode}
                aria-disabled={homeModeSelectionDisabled}
                className={cn(
                  desktopControlButtonClassName,
                  mode === 'home'
                    ? 'bg-primary text-primary-foreground'
                    : userHasHomeAddress
                      ? 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground'
                      : 'text-muted-foreground border border-border/80 bg-muted/40 opacity-65 cursor-not-allowed'
                )}
              >
                <Home className="h-3.5 w-3.5" />
                {homeButtonLabel}
              </button>
            )}
            <button
              onClick={handleGpsMode}
              className={cn(desktopControlButtonClassName, mode === 'gps' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground')}
            >
              <GpsButtonIcon className="h-3.5 w-3.5" />
              <span className="max-w-[11rem] truncate">
                {mode === 'gps' ? gpsCapturedLabel : 'Mi ubicación'}
              </span>
            </button>
          </div>
        </div>
        {!loading && displayStores.length > 0 && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className={controlGroupClassName}>
              <button
                type="button"
                onClick={() => { setViewMode('carousel'); onViewModeChange?.('carousel'); }}
                className={cn(viewButtonDesktopClassName, viewMode === 'carousel' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground')}
              >
                Carrusel
              </button>
              <button
                type="button"
                onClick={() => { setViewMode('directory'); onViewModeChange?.('directory'); }}
                className={cn(viewButtonDesktopClassName, viewMode === 'directory' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground')}
              >
                Directorio
              </button>
            </div>

            <div className={controlGroupClassName}>
              <button
                type="button"
                onClick={() => setStoreStatusFilter('todas')}
                className={cn(viewButtonDesktopClassName, storeStatusFilter === 'todas' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground')}
              >
                Todas
              </button>
              <button
                type="button"
                onClick={handleOpenFilterClick}
                className={cn(viewButtonDesktopClassName, storeStatusFilter === 'abiertas' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground')}
              >
                Abiertas
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {!loading && (
      <div className="mb-2 md:hidden relative w-full">
        <div className="-mx-2 max-[430px]:-mx-1 overflow-x-auto pb-0.5 scrollbar-hide">
          <div className="inline-flex min-w-max items-center gap-2 px-2 max-[430px]:px-1">
            <div className={cn(controlGroupClassName, 'flex-shrink-0')}>
            <button
              onClick={handleLandmarkMode}
              className={cn(mobileControlButtonClassName, mode === 'landmark' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground')}
            >
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="max-w-[8.5rem] truncate">
                {homeLandmarkLabel}
              </span>
            </button>
            {userCanUseHomeMode && (
              <button
                onClick={handleHomeMode}
                aria-disabled={homeModeSelectionDisabled}
                className={cn(
                  mobileControlButtonClassName,
                  mode === 'home'
                    ? 'bg-primary text-primary-foreground'
                    : userHasHomeAddress
                      ? 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground'
                      : 'text-muted-foreground border border-border/80 bg-muted/40 opacity-65 cursor-not-allowed'
                )}
              >
                <Home className="h-3.5 w-3.5" />
                {homeButtonLabel}
              </button>
            )}
            <button
              onClick={handleGpsMode}
              className={cn(mobileControlButtonClassName, mode === 'gps' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground')}
            >
              <GpsButtonIcon className="h-3.5 w-3.5" />
              <span className="max-w-[8.5rem] truncate">
                {mode === 'gps' ? gpsCapturedLabel : 'Mi ubicación'}
              </span>
            </button>
            </div>

            {!loading && displayStores.length > 0 && (
              <div className={cn(controlGroupClassName, 'flex-shrink-0')}>
              <button
                type="button"
                onClick={() => { setViewMode('carousel'); onViewModeChange?.('carousel'); }}
                className={cn(viewButtonMobileClassName, viewMode === 'carousel' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground')}
              >
                Carrusel
              </button>
              <button
                type="button"
                onClick={() => { setViewMode('directory'); onViewModeChange?.('directory'); }}
                className={cn(viewButtonMobileClassName, viewMode === 'directory' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground')}
              >
                Directorio
              </button>
              </div>
            )}
          
            {!loading && displayStores.length > 0 && (
              <div className={cn(controlGroupClassName, 'flex-shrink-0')}>
              <button
                type="button"
                onClick={() => setStoreStatusFilter('todas')}
                className={cn(viewButtonMobileClassName, storeStatusFilter === 'todas' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground')}
              >
                Todas
              </button>
              <button
                type="button"
                onClick={handleOpenFilterClick}
                className={cn(viewButtonMobileClassName, storeStatusFilter === 'abiertas' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground')}
              >
                Abiertas
              </button>
              </div>
            )}
          </div>
        </div>
        {!loading && displayStores.length > 0 && viewMode === 'carousel' && (
          <div className="flex items-center justify-end gap-2">
          </div>
        )}
      </div>
      )}

      {!loading && (
      <div className="relative mt-2 overflow-visible hidden md:block">
        <div className="hidden md:grid md:grid-cols-[1fr_auto] md:items-center md:gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={controlGroupClassName}>
              <button
                onClick={handleLandmarkMode}
                className={cn(desktopControlButtonClassName, mode === 'landmark' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground')}
              >
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                <span className="max-w-[13rem] truncate">
                  {homeLandmarkLabel}
                </span>
              </button>
              {userCanUseHomeMode && (
                <button
                  onClick={handleHomeMode}
                  aria-disabled={homeModeSelectionDisabled}
                  className={cn(
                    desktopControlButtonClassName,
                    mode === 'home'
                      ? 'bg-primary text-primary-foreground'
                      : userHasHomeAddress
                        ? 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground'
                        : 'text-muted-foreground border border-border/80 bg-muted/40 opacity-65 cursor-not-allowed'
                  )}
                >
                  <Home className="h-3.5 w-3.5" />
                  {homeButtonLabel}
                </button>
              )}
              <button
                onClick={handleGpsMode}
                className={cn(desktopControlButtonClassName, mode === 'gps' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground')}
              >
                <GpsButtonIcon className="h-3.5 w-3.5" />
                <span className="max-w-[11rem] truncate">
                  {mode === 'gps' ? gpsCapturedLabel : 'Mi ubicación'}
                </span>
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 justify-self-end">
            <div className={controlGroupClassName}>
              <button
                type="button"
                onClick={() => setStoreStatusFilter('todas')}
                className={cn(viewButtonDesktopClassName, storeStatusFilter === 'todas' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground')}
              >
                Todas
              </button>
              <button
                type="button"
                onClick={handleOpenFilterClick}
                className={cn(viewButtonDesktopClassName, storeStatusFilter === 'abiertas' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground')}
              >
                Abiertas
              </button>
            </div>
            <div className={controlGroupClassName}>
              <button
                type="button"
                onClick={() => { setViewMode('carousel'); onViewModeChange?.('carousel'); }}
                className={cn(viewButtonDesktopClassName, viewMode === 'carousel' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground')}
              >
                Carrusel
              </button>
              <button
                type="button"
                onClick={() => { setViewMode('directory'); onViewModeChange?.('directory'); }}
                className={cn(viewButtonDesktopClassName, viewMode === 'directory' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-primary/15 dark:hover:bg-primary/25 hover:text-primary-foreground')}
              >
                Directorio
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      {loading && (
        <div className="relative space-y-4 pt-0 pb-2" aria-hidden="true">
          <div className="mb-2 md:mb-4 px-2 max-[430px]:px-1 md:px-0">
            <Skeleton className="h-11 md:h-12 w-full max-w-[28rem] rounded-full shadow-sm" />
          </div>

          {viewMode === 'carousel' ? (
            <div className="space-y-4 px-2 max-[430px]:px-1 md:px-0">
              <div className="flex gap-2.5 overflow-x-hidden overflow-y-visible pt-1 pb-1 md:gap-4 md:pt-2">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <div
                    key={`store-skeleton-${idx}`}
                    className="w-[196px] flex-shrink-0 px-1.5 py-1.5 sm:w-[220px] md:w-[312px] md:px-2 lg:w-[340px]"
                  >
                    <div className="relative w-full h-full rounded-2xl border border-primary/15">
                      <div className="rounded-2xl bg-gradient-to-br from-card via-card to-primary/5 p-3 sm:p-3.5 flex flex-col gap-3 text-center overflow-hidden h-[174px] sm:h-[182px] [clip-path:inset(0_round_1rem)]">
                        <div className="flex items-start justify-between gap-2 relative z-10">
                          <Skeleton className="h-12 w-12 rounded-xl shrink-0" />
                          <Skeleton className="h-5 w-14 rounded-full shrink-0" />
                        </div>

                        <div className="w-full relative z-10 text-left">
                          <Skeleton className="h-4 w-24 rounded" />
                          <div className="mt-1.5 flex items-start gap-2">
                            <Skeleton className="mt-1 h-2 w-2 rounded-full shrink-0" />
                            <div className="flex flex-col gap-1.5 min-w-0 flex-1">
                              <Skeleton className="h-3 w-20 rounded" />
                              <Skeleton className="h-3 w-24 rounded" />
                            </div>
                          </div>
                        </div>

                        <div className="mt-auto flex items-center gap-1.5 overflow-hidden whitespace-nowrap">
                          <Skeleton className="h-5 w-16 rounded-full shrink-0" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="rounded-3xl border border-primary/15 bg-gradient-to-br from-card via-card to-primary/5 p-4 shadow-sm">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr,280px] lg:gap-6">
                  <div className="space-y-3">
                    <Skeleton className="h-5 w-32 rounded" />
                    <Skeleton className="h-3.5 w-full max-w-2xl rounded" />
                    <Skeleton className="h-3.5 w-2/3 rounded" />
                  </div>
                  <div className="space-y-2 rounded-2xl border border-primary/10 bg-background/80 p-3">
                    <Skeleton className="h-4 w-20 rounded" />
                    <Skeleton className="h-8 w-full rounded-xl" />
                    <div className="flex gap-2">
                      <Skeleton className="h-7 w-20 rounded-xl" />
                      <Skeleton className="h-7 w-24 rounded-xl" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 px-2 max-[430px]:px-1 md:px-0">
              {Array.from({ length: 3 }).map((_, sectionIndex) => (
                <div key={`directory-skeleton-section-${sectionIndex}`} className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <Skeleton className="h-6 w-28 rounded" />
                    <Skeleton className="h-8 w-24 rounded-full" />
                  </div>
                  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 md:gap-3 xl:grid-cols-3">
                    {Array.from({ length: 3 }).map((__, cardIndex) => (
                      <div
                        key={`directory-skeleton-card-${sectionIndex}-${cardIndex}`}
                        className="flex min-h-[168px] flex-col gap-3 rounded-2xl border border-primary/15 bg-gradient-to-br from-card via-card to-primary/5 p-3"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex min-w-0 items-center gap-2">
                            <Skeleton className="h-11 w-11 rounded-xl shrink-0" />
                            <div className="min-w-0 space-y-1.5">
                              <Skeleton className="h-4 w-24 rounded" />
                              <Skeleton className="h-3 w-16 rounded" />
                            </div>
                          </div>
                          <Skeleton className="h-5 w-12 rounded-full" />
                        </div>

                        <div className="space-y-2">
                          <Skeleton className="h-3.5 w-5/6 rounded" />
                          <Skeleton className="h-3.5 w-3/5 rounded" />
                        </div>

                        <div className="mt-auto flex flex-nowrap gap-1.5 overflow-hidden whitespace-nowrap">
                          <Skeleton className="h-5 w-14 rounded-full shrink-0" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!loading && gpsError && (
        <p className="text-sm text-destructive px-1 py-2">{gpsError}</p>
      )}

      {!loading && showEmpty && (
        <div className="px-1 py-2 space-y-1">
          <p className="text-sm text-muted-foreground">
            {mode === 'gps'
              ? 'No hay tiendas disponibles para tu ubicacion actual.'
              : 'No hay tiendas con direccion registrada en tu zona todavia.'}
          </p>
          {mode === 'gps' && pinnedStore?.id && (
            <p className="text-xs text-primary font-medium">Mostrando Minibid por defecto.</p>
          )}
        </div>
      )}

      {!loading && displayStores.length > 0 && (
        <div
          className="relative pt-0 pb-2"
        >
          {viewMode === 'carousel' ? (
            <>
              <div className={cn('relative px-2 max-[430px]:px-1 md:px-0', hasCarouselOverflow && '-mx-2 md:mx-0')}>
                <div className="relative">
                  <div
                    ref={scrollContainerRef}
                    className="w-full flex gap-2.5 md:gap-4 overflow-x-auto overflow-y-visible pt-2 md:pt-3 pb-1 snap-x snap-mandatory scrollbar-hide"
                    onPointerDown={handleCarouselPointerDown}
                    onPointerMove={handleCarouselPointerMove}
                    onPointerUp={handleCarouselPointerEnd}
                    onPointerCancel={handleCarouselPointerEnd}
                    onMouseEnter={(e) => {
                      syncHoveredCardFromPoint(e.clientX, e.clientY);
                    }}
                    onMouseMove={(e) => {
                      syncHoveredCardFromPoint(e.clientX, e.clientY);
                    }}
                    onMouseLeave={() => {
                      setHoveredCardKey(null);
                    }}
                  >
                  {virtualCarouselStores.map(({ store, virtualIndex }) => {
                    const isSelected = !promoCardSelected && selectedStoreId === store.id;
                    const cardHoverKey = `store-${virtualIndex}`;
                    const baseScale = 1;
                    const effectiveStore = getEffectiveStoreScheduleData(store);
                    const openState = storeOpenMap[store.id];
                    const scheduleLoaded = storeScheduleValidityMap[store.id] === true;
                    const closing = openState === true ? getTodayClosingTime(effectiveStore) : null;
                    const next = openState === false ? getNextOpeningTime(effectiveStore) : null;
                    const { dayIdx: todayIdx } = getStoreNowParts(getStoreWeekAndTimezone(effectiveStore).tz);
                    const dayNames = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo'];

                    return (
                      <button
                        key={`${store.id}-${virtualIndex}`}
                        ref={(el) => { carouselCardRefs.current[virtualIndex] = el; }}
                        data-card-hover-key={cardHoverKey}
                        data-store-id={store.id}
                        type="button"
                        onClick={(event) => {
                          if (suppressCardClickRef.current) {
                            event.preventDefault();
                            return;
                          }
                          handleStoreSelect(store);
                        }}
                        onMouseEnter={() => {
                          setHoveredCardKey(cardHoverKey);
                        }}
                        onMouseMove={() => {
                          setHoveredCardKey(cardHoverKey);
                        }}
                        onMouseLeave={() => {
                          setHoveredCardKey((current) => (current === cardHoverKey ? null : current));
                        }}
                        className="relative z-10 hover:z-20 focus-visible:z-20 flex-shrink-0 snap-center w-[196px] sm:w-[220px] md:w-[312px] lg:w-[340px] group px-1.5 md:px-2 py-1.5 rounded-2xl"
                        style={{ perspective: '600px' }}
                      >
                        <ThreeDEffect
                          baseScale={baseScale}
                          activeOverride={hoveredCardKey === cardHoverKey}
                          transformOrigin={virtualIndex === 0 ? 'left center' : 'center center'}
                          className={cn(
                            'relative w-full h-full rounded-2xl border',
                            isSelected ? 'border-primary shadow ring-2 ring-primary/25' : 'border-primary/15 hover:border-primary/45 hover:shadow'
                          )}
                        >
                          <div className="rounded-2xl bg-gradient-to-br from-card via-card to-primary/5 p-3 sm:p-3.5 flex flex-col gap-3 text-center overflow-hidden h-[174px] sm:h-[182px] [clip-path:inset(0_round_1rem)]">
                            <div className="flex items-start justify-between gap-2 relative z-10">
                              <Avatar className="h-12 w-12 rounded-xl ring-2 ring-primary/15">
                                <AvatarImage src={store.avatar_url || undefined} alt={store.name} className="object-cover" />
                                <AvatarFallback className="rounded-xl bg-primary/10 text-primary font-bold text-lg">
                                  {store.name.charAt(0).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              {store.distance !== undefined && (
                                <span
                                  role="button"
                                  tabIndex={0}
                                  className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-background/90 px-2 py-0.5 text-[11px] font-semibold text-muted-foreground cursor-pointer"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleDistanceChipClick(store);
                                  }}
                                  onKeyDown={(event) => {
                                    if (event.key !== 'Enter' && event.key !== ' ') return;
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleDistanceChipClick(store);
                                  }}
                                >
                                  <MapPin className="h-3 w-3 text-primary" />
                                  {formatDistance(store.distance)}
                                </span>
                              )}
                            </div>
                            <div className="w-full relative z-10 text-left">
                              <p className="font-semibold text-sm sm:text-[15px] line-clamp-2 leading-tight group-hover:text-primary transition-colors">
                                {store.name}
                              </p>
                              <div className="mt-1.5 text-[11px]">
                                {openState === true ? (
                                  <div className="flex items-start gap-2 text-foreground">
                                    <span className="mt-1 h-2 w-2 rounded-full bg-success shrink-0"></span>
                                    <div className="flex flex-col leading-tight">
                                      <div className="font-medium">Abierto ahora</div>
                                      {closing ? <div className="text-muted-foreground">Cierra a {closing}</div> : null}
                                    </div>
                                  </div>
                                ) : openState === false ? (
                                  <div className="flex items-start gap-2 text-foreground">
                                    <span className="mt-1 h-2 w-2 rounded-full bg-error shrink-0"></span>
                                    <div className="flex flex-col leading-tight">
                                      <div className="font-medium">Cerrado</div>
                                      {next ? (
                                        <div className="text-muted-foreground">
                                          Abre {next.dayIdx === todayIdx ? 'a' : `${dayNames[next.dayIdx]} a`} {next.time}
                                        </div>
                                      ) : (
                                        <div className="text-muted-foreground">Horario no disponible</div>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-start gap-2 text-foreground">
                                    <span className="mt-1 h-2 w-2 rounded-full bg-warning shrink-0"></span>
                                    <div className="flex flex-col leading-tight">
                                      <div className="font-medium">{scheduleLoaded ? 'Horario no disponible' : 'Horario no cargado'}</div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            <KeywordChipsMarquee
                              keywords={storeKeywords[store.id] || []}
                              storeId={store.id}
                              animate={
                                isSelected ||
                                hoveredCardKey === cardHoverKey ||
                                (isMobileViewport && viewMode === 'carousel' && virtualIndex === currentCarouselIndex)
                              }
                            />
                          </div>
                        </ThreeDEffect>
                      </button>
                    );
                  })}

                  {/* Promo card - rendered separately to avoid click interference */}
                  <button
                    key="placeholder-your-store"
                    data-card-hover-key="promo"
                    ref={(el) => {
                      if (promoVirtualIndex >= 0) {
                        carouselCardRefs.current[promoVirtualIndex] = el;
                      }
                    }}
                    type="button"
                    onClick={(e) => {
                      if (suppressCardClickRef.current) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }
                      e.stopPropagation();
                      handlePromoCardToggle();
                    }}
                    onMouseEnter={() => {
                      setHoveredCardKey('promo');
                    }}
                    onMouseMove={() => {
                      setHoveredCardKey('promo');
                    }}
                    onMouseLeave={() => {
                      setHoveredCardKey((current) => (current === 'promo' ? null : current));
                    }}
                    className="relative z-10 hover:z-20 focus-visible:z-20 flex-shrink-0 snap-center w-[196px] sm:w-[220px] md:w-[312px] lg:w-[340px] group px-1.5 md:px-2 py-1.5"
                    style={{ perspective: '600px' }}
                  >
                    <ThreeDEffect
                      baseScale={1}
                      activeOverride={hoveredCardKey === 'promo'}
                      className={cn(
                        'relative rounded-2xl border',
                        promoCardSelected 
                          ? 'border-primary shadow ring-2 ring-primary/25' 
                          : 'border-primary/15 hover:border-primary/45 hover:shadow'
                      )}
                    >
                      <div className="rounded-2xl bg-gradient-to-br from-card via-card to-primary/5 p-3 sm:p-3.5 flex flex-col gap-3 text-center overflow-hidden h-[174px] sm:h-[182px] [clip-path:inset(0_round_1rem)]">
                        <div className="flex items-start justify-between gap-2 relative z-10">
                          {renderPromoRentButton('compact')}
                          <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-background/90 px-2 py-0.5 text-[11px] font-semibold text-primary">
                            Minibid.org
                          </span>
                        </div>

                        <div className="w-full relative z-10 text-left">
                          <p className="font-semibold text-sm sm:text-[15px] line-clamp-2 leading-tight group-hover:text-primary transition-colors">
                            Tu tienda virtual en Minibid
                          </p>
                          <span className="mt-1 inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-1.5 py-[2px] text-[9px] leading-none font-bold text-primary">
                            Nuevo
                          </span>
                        </div>

                        <KeywordChipsMarquee
                          keywords={['Gestiona stock', 'Cobros', 'Canastos grupales', 'Envíos']}
                          storeId="promo-minibid"
                          animate={promoCardSelected || hoveredCardKey === 'promo'}
                        />
                      </div>
                    </ThreeDEffect>
                  </button>
                  </div>

                  <div className="pointer-events-none absolute inset-y-0 left-2 right-2 max-[390px]:left-3 max-[390px]:right-3 z-30 flex items-center justify-between">
                    <div className="pointer-events-auto pl-0.5 max-[390px]:pl-1">
                      {renderChevronButton('left', canScrollLeft, true)}
                    </div>
                    <div className="pointer-events-auto pr-0.5 max-[390px]:pr-1">
                      {renderChevronButton('right', canScrollRight, true)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative mt-1 overflow-visible">
                {promoCardSelected && (
                <div ref={promoDetailsRef} className="relative left-0 right-0 mt-4 mb-8 md:mb-6 z-40 rounded-3xl border border-primary/20 overflow-hidden bg-card">
                  <div className="bg-gradient-to-br from-card via-card to-primary/10 p-4 sm:p-5 pb-4 md:pb-6 relative overflow-hidden isolate [clip-path:inset(0_round_1.5rem)] [transform:translateZ(0)] [backface-visibility:hidden]">
                    <div className="absolute -right-10 -top-14 h-36 w-36 rounded-full bg-primary/15 blur-3xl pointer-events-none" />
                    <div className="absolute -left-8 -bottom-8 h-24 w-24 rounded-full bg-primary/10 blur-xl pointer-events-none" />

                    <div className="relative z-10 flex flex-col gap-4 items-start">
                      {renderPromoRentButton('expanded')}

                      <div className="space-y-2 min-w-0 w-full">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight line-clamp-1">Tu tienda aquí</h2>
                          <span className="text-[9px] leading-none px-1.5 py-[2px] rounded-full border border-primary/20 bg-background/80 text-primary font-semibold">
                            En pocos minutos
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground max-w-2xl">
                          Creá tu tienda pública en internet y activá herramientas reales para vender más dentro de Minibid.org. Permití a tus vecinos comprar desde casa con envío propio, a cargo de Minibid o retiro en tienda.
                        </p>

                        <div className="grid grid-cols-1 gap-2 pt-2 lg:grid-cols-2">
                          <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 lg:col-span-2">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <Store className="h-3.5 w-3.5 text-primary" />
                                <p className="text-xs font-semibold text-foreground">Producto tipo rental</p>
                              </div>
                              <span className="inline-flex items-center rounded-full border border-primary/30 bg-background px-2 py-0.5 text-[10px] font-bold text-primary">
                                $1.000/día
                              </span>
                            </div>
                          </div>

                          <div className="rounded-lg border border-primary/15 bg-background/75 px-3 py-2">
                            <div className="flex items-center gap-2">
                              <Package className="h-3.5 w-3.5 text-primary" />
                              <p className="text-xs font-semibold text-foreground">Gestión de stock</p>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5">Controla disponibilidad y evita quiebres en tiempo real.</p>
                          </div>
                          <div className="rounded-lg border border-primary/15 bg-background/75 px-3 py-2">
                            <div className="flex items-center gap-2">
                              <CreditCard className="h-3.5 w-3.5 text-primary" />
                              <p className="text-xs font-semibold text-foreground">Cobros integrados</p>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5">Recibe pagos con flujo simple y seguimiento de órdenes.</p>
                          </div>
                          <div className="rounded-lg border border-primary/15 bg-background/75 px-3 py-2">
                            <div className="flex items-center gap-2">
                              <Users className="h-3.5 w-3.5 text-primary" />
                              <p className="text-xs font-semibold text-foreground">Canastos grupales</p>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5">Varias personas compran juntas en un mismo canasto.</p>
                          </div>
                          <div className="rounded-lg border border-primary/15 bg-background/75 px-3 py-2">
                            <div className="flex items-center gap-2">
                              <Truck className="h-3.5 w-3.5 text-primary" />
                              <p className="text-xs font-semibold text-foreground">Envíos desde tu tienda</p>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5">Activa reparto local y coordina entregas de forma clara.</p>
                          </div>
                          <div className="rounded-lg border border-primary/15 bg-background/75 px-3 py-2">
                            <div className="flex items-center gap-2">
                              <Link className="h-3.5 w-3.5 text-primary" />
                              <p className="text-xs font-semibold text-foreground">Enlace personalizado</p>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5">Personalizá tu enlace directo incluído minibid.org/elnombredetutienda/</p>
                          </div>
                          <div className="rounded-lg border border-primary/15 bg-background/75 px-3 py-2">
                            <div className="flex items-center gap-2">
                              <QrCode className="h-3.5 w-3.5 text-primary" />
                              <p className="text-xs font-semibold text-foreground">Carga de productos</p>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5">Subí productos manualmente o a través del código de barras, con nuestro catálogo precargado.</p>
                          </div>

                          <div className="pt-1 flex justify-end lg:col-span-2">
                            <Button
                              type="button"
                              disabled={!onRentStoreClick || rentStoreLoading}
                              title={rentStoreLoading ? 'Agregando...' : 'Agregar alquiler de tienda'}
                              onClick={(event) => {
                                event.stopPropagation();
                                void onRentStoreClick?.();
                              }}
                              className={cn(
                                'h-[43px] rounded-full px-3 shadow-lg hover:shadow-xl transition-all active:scale-90 duration-75 inline-flex items-center gap-1.5 justify-center',
                                onRentStoreClick
                                  ? 'disabled:opacity-70'
                                  : 'opacity-60 cursor-not-allowed'
                              )}
                            >
                              <Plus className="h-6 w-6" />
                              <span className="text-xs font-semibold">Alquilar tienda</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              </div>

            </>
          ) : (
            <div className="space-y-5">
              {alphabetDirectory.map((group) => (
                <section key={group.letter} className="space-y-2.5">
                  <div className="sticky top-0 z-10 rounded-md px-2 py-1">
                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full border border-primary/25 px-2 text-xs font-bold text-primary">
                      {group.letter}
                    </span>
                  </div>

                  {(() => {
                    const renderCard = (store: NearbyStore) => {
                      const isSelected = selectedStoreId === store.id;
                      const showAsSelected = isSelected && (hasManualStoreSelection || selectedStoreId !== pinnedStore?.id);
                      const effectiveStore = getEffectiveStoreScheduleData(store);
                      const openState = storeOpenMap[store.id];
                      const scheduleLoaded = storeScheduleValidityMap[store.id] === true;
                      const closing = openState === true ? getTodayClosingTime(effectiveStore) : null;
                      const next = openState === false ? getNextOpeningTime(effectiveStore) : null;
                      const { dayIdx: todayIdx } = getStoreNowParts(getStoreWeekAndTimezone(effectiveStore).tz);
                      const dayNames = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo'];
                      return (
                        <button
                          key={store.id}
                          type="button"
                          onClick={() => handleStoreSelect(store)}
                          onPointerEnter={() => setHoveredCardKey(`directory-${store.id}`)}
                          onPointerLeave={() => setHoveredCardKey((current) => (current === `directory-${store.id}` ? null : current))}
                          className="relative group w-full px-1.5 py-1.5"
                          style={{ perspective: '600px' }}
                        >
                          <ThreeDEffect
                            baseScale={showAsSelected ? 1.02 : 1}
                            className={cn(
                              'relative rounded-2xl border',
                              showAsSelected ? 'border-primary shadow ring-2 ring-primary/25' : 'border-primary/15 hover:border-primary/45 hover:shadow'
                            )}
                          >
                            <div className="rounded-2xl bg-gradient-to-br from-card via-card to-primary/5 p-3 sm:p-3.5 flex flex-col gap-3 overflow-hidden min-h-[174px] [clip-path:inset(0_round_1rem)]">
                              <div className="flex items-start justify-between gap-2 relative z-10">
                                <Avatar className="h-12 w-12 rounded-xl ring-2 ring-primary/15">
                                  <AvatarImage src={store.avatar_url || undefined} alt={store.name} className="object-cover" />
                                  <AvatarFallback className="rounded-xl bg-primary/10 text-primary font-bold text-lg">
                                    {store.name.charAt(0).toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>
                                {store.distance !== undefined && (
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-background/90 px-2 py-0.5 text-[11px] font-semibold text-muted-foreground cursor-pointer"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      handleDistanceChipClick(store);
                                    }}
                                    onKeyDown={(event) => {
                                      if (event.key !== 'Enter' && event.key !== ' ') return;
                                      event.preventDefault();
                                      event.stopPropagation();
                                      handleDistanceChipClick(store);
                                    }}
                                  >
                                    <MapPin className="h-3 w-3 text-primary" />
                                    {formatDistance(store.distance)}
                                  </span>
                                )}
                              </div>
                              <div className="w-full relative z-10 text-left">
                                <p className="font-semibold text-sm sm:text-[15px] line-clamp-2 leading-tight group-hover:text-primary transition-colors">
                                  {store.name}
                                </p>
                                <div className="mt-1.5 mb-1.5 text-[11px]">
                                  {openState === true ? (
                                    <div className="flex items-start gap-2 text-foreground">
                                      <span className="mt-1 h-2 w-2 rounded-full bg-success shrink-0"></span>
                                      <div className="flex flex-col leading-tight">
                                        <div className="font-medium">Abierto ahora</div>
                                        {closing ? <div className="text-muted-foreground">Cierra a {closing}</div> : null}
                                      </div>
                                    </div>
                                  ) : openState === false ? (
                                    <div className="flex items-start gap-2 text-foreground">
                                      <span className="mt-1 h-2 w-2 rounded-full bg-error shrink-0"></span>
                                      <div className="flex flex-col leading-tight">
                                        <div className="font-medium">Cerrado</div>
                                        {next ? (
                                          <div className="text-muted-foreground">
                                            Abre {next.dayIdx === todayIdx ? 'a' : `${dayNames[next.dayIdx]} a`} {next.time}
                                          </div>
                                        ) : (
                                          <div className="text-muted-foreground">Horario no disponible</div>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex items-start gap-2 text-foreground">
                                      <span className="mt-1 h-2 w-2 rounded-full bg-warning shrink-0"></span>
                                      <div className="flex flex-col leading-tight">
                                        <div className="font-medium">{scheduleLoaded ? 'Horario no disponible' : 'Horario no cargado'}</div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                {showAsSelected ? (
                                  <p className="text-[11px] text-primary font-semibold mt-1">Seleccionada</p>
                                ) : (
                                  <p className="text-[11px] mt-1.5 text-muted-foreground">Productos destacados</p>
                                )}
                              </div>
                              <KeywordChipsMarquee
                                keywords={storeKeywords[store.id] || []}
                                storeId={store.id}
                                animate={showAsSelected || hoveredCardKey === `directory-${store.id}`}
                              />
                            </div>
                          </ThreeDEffect>
                        </button>
                      );
                    };

                    const selectedIdx = group.stores.findIndex((s) => s.id === selectedStoreId);
                    const hasExpansion = selectedIdx !== -1 && hasManualStoreSelection && !!renderStoreExpansion;

                    if (!hasExpansion) {
                      return (
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                          {group.stores.map(renderCard)}
                        </div>
                      );
                    }

                    const rowEndIdx = Math.min(
                      Math.ceil((selectedIdx + 1) / directoryColCount) * directoryColCount - 1,
                      group.stores.length - 1
                    );
                    const expandedStore = group.stores[selectedIdx];
                    const storesBefore = group.stores.slice(0, rowEndIdx + 1);
                    const storesAfter = group.stores.slice(rowEndIdx + 1);
                    const catalogOpen = catalogOpenStoreId === expandedStore.id;

                    return (
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                        {storesBefore.map(renderCard)}
                        <div className="col-span-full rounded-2xl border border-primary/20 overflow-hidden mb-[14rem] sm:mb-0">
                          {renderStoreExpansion!(expandedStore)}
                          {renderStoreCatalog && (
                            <div className="px-4 py-3 border-t border-primary/10 bg-card flex items-center">
                              <button
                                type="button"
                                onClick={() => setCatalogOpenStoreId(catalogOpen ? null : expandedStore.id)}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/10 text-primary text-sm font-semibold hover:bg-primary/20 transition-colors"
                              >
                                <ChevronRight className={cn('h-4 w-4 transition-transform', catalogOpen && 'rotate-90')} />
                                {catalogOpen ? 'Ocultar catálogo' : 'Mostrar catálogo'}
                              </button>
                            </div>
                          )}
                          {renderStoreCatalog && catalogOpen && (
                            <div className="p-4 border-t border-primary/10">
                              {renderStoreCatalog(expandedStore)}
                            </div>
                          )}
                        </div>
                        {storesAfter.map(renderCard)}
                      </div>
                    );
                  })()}
                </section>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
